import { BaseFieldFilter, NativeFieldFilter } from 'api/dashboard/widget';
import { NumberField } from 'components/organisms/tasks/fields';
import {
  NumberFieldFilter,
  NumberFilterOperationType,
} from 'components/organisms/tasks/filters/number';
import {
  NumberOperationType,
  Operation,
} from 'components/organisms/tasks/filters/operations';
import { unreachable } from '../../unreachable';

const toOperation = (filter: NumberFieldFilter): NumberOperationType => {
  const { type } = filter;

  switch (type) {
    case NumberFilterOperationType.IsEmpty:
      return NumberOperationType.IsEmpty;
    case NumberFilterOperationType.IsNotEmpty:
      return NumberOperationType.IsNotEmpty;
    case NumberFilterOperationType.IsGreaterThan:
      return NumberOperationType.GreaterThan;
    case NumberFilterOperationType.Is:
      return NumberOperationType.Value;
    case NumberFilterOperationType.IsNotExactly:
      return NumberOperationType.IsNot;
    case NumberFilterOperationType.IsLessThan:
      return NumberOperationType.LessThan;
    default:
      return unreachable(type);
  }
};

const toApiFormat = (filter: NumberFieldFilter): BaseFieldFilter => {
  switch (filter.type) {
    case NumberFilterOperationType.IsEmpty:
      return {
        operation: Operation.IsEmpty,
        sourceType: filter.field.sourceType,
      };
    case NumberFilterOperationType.IsNotEmpty:
      return {
        operation: Operation.IsNotEmpty,
        sourceType: filter.field.sourceType,
      };
    case NumberFilterOperationType.IsGreaterThan:
      return {
        operation: Operation.GreaterThan,
        value: String(filter.value),
        sourceType: filter.field.sourceType,
      };
    case NumberFilterOperationType.Is:
      return {
        operation: Operation.Value,
        value: String(filter.value),
        sourceType: filter.field.sourceType,
      };
    case NumberFilterOperationType.IsNotExactly:
      return {
        operation: Operation.IsNot,
        value: String(filter.value),
        sourceType: filter.field.sourceType,
      };
    case NumberFilterOperationType.IsLessThan:
      return {
        operation: Operation.LessThan,
        value: String(filter.value),
        sourceType: filter.field.sourceType,
      };
    default:
      return unreachable(filter);
  }
};

const toType = (operation: NumberOperationType): NumberFilterOperationType => {
  switch (operation) {
    case NumberOperationType.Value:
      return NumberFilterOperationType.Is;
    case NumberOperationType.IsNot:
      return NumberFilterOperationType.IsNotExactly;
    case NumberOperationType.IsEmpty:
      return NumberFilterOperationType.IsEmpty;
    case NumberOperationType.IsNotEmpty:
      return NumberFilterOperationType.IsNotEmpty;
    case NumberOperationType.GreaterThan:
      return NumberFilterOperationType.IsGreaterThan;
    case NumberOperationType.LessThan:
      return NumberFilterOperationType.IsLessThan;
    default:
      return unreachable(operation);
  }
};

const parse = (
  filter: NativeFieldFilter,
  field: NumberField,
): NumberFieldFilter => {
  const type = toType(filter.operation as unknown as NumberOperationType);

  switch (type) {
    case NumberFilterOperationType.IsEmpty:
    case NumberFilterOperationType.IsNotEmpty:
      return { field, type };
    case NumberFilterOperationType.IsGreaterThan:
    case NumberFilterOperationType.Is:
    case NumberFilterOperationType.IsNotExactly:
    case NumberFilterOperationType.IsLessThan: {
      const number = JSON.parse(`"${filter.value}"`) as number | null;

      return {
        field,
        type,
        value: number == null ? undefined : Number(number),
      };
    }
    default:
      return unreachable(type);
  }
};

export const NumberFilter = {
  parse,
  toOperation,
  toApiFormat,
};
